import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  AutoComplete,
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  List,
  Modal,
  notification,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "shared/redux/store";
import { debounce } from "lodash";
import { FaSearch } from "react-icons/fa";
import { setScreenHeader } from "shared/redux/common-reducer";
import {
  setFollowUpIsShowFilter,
  setFollowUpPageNo,
  setFollowUpPageSize,
  setFollowUpSearchParam,
} from "shared/redux/followUp-reducer";
import blackFilterIcon from "../../assets/images/blackFilter.svg";
import LeadFilter from "shared/components/Lead/LeadFilter/LeadFilter";
import API_SERVICE from "shared/services/api-service";
import type { ColumnsType } from "antd/es/table";
import { getMaskedMobile, hasUserRole } from "shared/services/Utility";
import { ReactComponent as IconDialler } from "../../assets/images/dialler.svg";
import { ReactComponent as IconCall } from "../../assets/images/call.svg";
import { ReactComponent as IconPlayNewBorder } from "../../assets/images/play_new_border.svg";
import { ReactComponent as IconCustomer360 } from "../../assets/images/Customer360.svg";
import Customer360 from "shared/components/LeadNew/LeadDetail/Customer360";
import OIButton from "shared/ComponentsOI/OIButton/OIButton";
import "./FollowUp.scss";

type Props = {};

const FollowUp: React.FunctionComponent<Props> = () => {
  const userRolePermission = hasUserRole();
  const dispatch = useDispatch();
  const history = useHistory();
  const [followUpForm] = Form.useForm();
  const [allLeadsData, setAllLeadsData] = useState([]) as any;
  const [loading, setLoading] = useState(false);
  const [selectedAssignTo, setSelectedAssignTo] = useState(null as any);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [assignToData, setAssignToData] = useState([] as any);
  const [errorDisplay, setErrorDisplay] = useState(false);
  const isCustomer360Enabled =
    localStorage.getItem("customer360Enabled") == "true";
  const [isOneView, setIsOneView] = useState(false);
  const [customerCaseId, setCustomerCaseId] = useState("");
  const [selectedLeadIds, setSelectedLeadIds] = useState([]) as any;
  const followUp: any = useSelector((state: RootState) => state.followUp);
  const { appColors, isMobile }: any = useSelector(
    (state: RootState) => state.common
  );
  const assignedToSalesOrderRef = useRef<string>();
  const sortFieldRef = useRef<string>();
  const diallerFollowUpDateRef = useRef<string>();
  const [sortState, setSortState] = useState<{
    field: string | null;
    order: "ascend" | "descend" | null;
  }>({
    field: null,
    order: null,
  });

  useEffect(() => {
    dispatch(setScreenHeader({ screenTitle: "Follow Up" }));
    followUpForm.setFieldsValue({
      searchBox: followUp.searchParam,
    });
    getFollowUpLeadsData(
      followUp.pageNo,
      followUp.pageSize,
      followUp.searchParam,
      followUp.filterData
    );
  }, [followUp.searchParam, followUp.filterData]);

  const getFollowUpLeadsData = (
    pageNo: number = 1,
    pageSize: number = 10,
    searchValue: any = null,
    filter: any = followUp.filterData
  ) => {
    let sortField = sortFieldRef.current === null || sortFieldRef.current === undefined ? "DIALLER_DISPOSITION_STATUS_DATETIME" : sortFieldRef.current
    let sortOrder = sortFieldRef.current === null || sortFieldRef.current === undefined ? "DESC" : sortFieldRef.current === "DIALLER_DISPOSITION_STATUS_DATETIME" 
                        ? diallerFollowUpDateRef.current: assignedToSalesOrderRef.current
    let params: any = {
      ...filter,
      searchParam: searchValue,
      sortColumn : sortField,
      sortOrder:  sortOrder
  }
    setLoading(true);
    setAllLeadsData([]);
    API_SERVICE.getLeadsByFilter(pageNo, pageSize, "FOLLOW_UP", params)
      .then(({ data }) => {
        const hasData = data?.payload?.content?.length > 0;
        if (!hasData) {
          notification.error({ message: "No data found." });
        }
        setTotalRecords(data?.payload?.totalElements);
        setAllLeadsData(data?.payload?.content);
      })
      .catch((error) => {
        notification.error({ message: API_SERVICE.handleErrors(error) });
      })
      .finally(() => setLoading(false));
  };

  const handleMobilePaginationChange = (current: any, pageSize: any) => {
    dispatch(setFollowUpPageNo(current));
    dispatch(setFollowUpPageSize(pageSize));
    getFollowUpLeadsData(current, pageSize, followUp.searchParam);
    window.scrollTo(0, 0);
  };

  const handlePaginationChange = (pagination) => {
    let page =
      pagination.pageSize !== followUp.pageSize ? 1 : pagination.current;
    dispatch(setFollowUpPageNo(page));
    dispatch(setFollowUpPageSize(pagination.pageSize));
    getFollowUpLeadsData(page, pagination.pageSize, followUp.searchParam);
    window.scrollTo(0, 0);
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const goToLeadDetails = (item: any) => {
    let caseId = item?.caseId;
    history.push(`/lead/details/${caseId}`);
  };

  const goToOneView = (item: any) => {
    if (isCustomer360Enabled) {
      setCustomerCaseId(item?.caseId);
      setIsOneView(true);
    } else {
      notification.error({
        message:
          "The Customer 360 feature is currently disabled. Contact the administration team to enable it",
      });
    }
  };

  const renderOneView = () => {
    return (
      <Modal
        title={<div style={{ textAlign: "center" }}>Customer 360</div>}
        width={1000}
        visible={isOneView}
        footer={null}
        closable
        onCancel={() => setIsOneView(false)}
      >
        <Customer360
          caseId={customerCaseId}
          customer360Data={null}
          isPAPQ={false}
        />
      </Modal>
    );
  };

  const bulkAssignError = () => {
    return (
      <Modal
        centered
        //style={{top:20}}
        width={350}
        footer={null}
        closable={false}
        visible={errorDisplay}
      >
        <div
          style={{ display: "grid", alignItems: "center", marginTop: "5px" }}
        >
          {/**/}
          <p>
            There was an error in assigning some cases. Please retry assignment
            for the cases highlighted on screen
          </p>
          <Button
            style={{
              bottom: "15px",
              justifyContent: "center",
              left: "70px",
            }}
            size="large"
            className="button-submit"
            htmlType="submit"
            onClick={() => setErrorDisplay(false)}
          >
            Cancel
          </Button>
        </div>
      </Modal>
    );
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record: any) => ({}),
    preserveSelectedRowKeys: true,
  };

  const colMobileNumber = (_: any, record: any) =>
    record?.contactDetails?.mobileNumber.startsWith("1")
      ? ""
      : getMaskedMobile(
          userRolePermission["MaskMobileNumber"],
          record?.contactDetails?.mobileNumber
        );

  const searchAssignTo = (ev: any) => {
    if (ev.length != 0) {
      const params = {
        name: ev,
      };
      API_SERVICE.getUsersWithRole(params).then(({ data }) => {
        if (data) {
          setAssignToData(data.payload);
        }
      });
    }
  };

  const selectAssignTo = (item, opt) => {
    setSelectedAssignTo(opt);
  };

  const assignSalesUserToLead = () => {
    if (isMobile) {
      if (selectedLeadIds?.length === 0) {
        notification.error({ message: "Please select a lead to approve." });
        return;
      }
    } else {
      if (undefined === selectedRowKeys || selectedRowKeys?.length === 0) {
        notification.error({ message: "Please select a lead to approve." });
        return;
      }
    }
    if (
      undefined === selectedAssignTo ||
      null === selectedAssignTo ||
      undefined === selectedAssignTo?.userId ||
      null === selectedAssignTo.userId
    ) {
      notification.error({ message: "Please assignee to assign a lead." });
      return;
    }
    if (
      (selectedRowKeys && selectedRowKeys?.length > 0) ||
      (selectedLeadIds?.length > 0 &&
        selectedAssignTo &&
        selectedAssignTo?.userId)
    ) {
      const params = {
        dsaCaseIds: isMobile ? selectedLeadIds : selectedRowKeys,
        salesUserId: selectedAssignTo?.userId,
      };
      API_SERVICE.assignSalesUserToLead(params)
        .then(({ data }) => {
          if (data) {
            let tmp = [""];
            tmp.push("salesUserId");
            setSelectedRowKeys([]);
            setSelectedLeadIds([]);
            if (data.payload.errorCases) {
              notification.error({ message: data.payload.message });
            } else {
              notification.success({ message: data.payload.message });
            }
            followUpForm.resetFields(tmp);
            getFollowUpLeadsData(
              followUp.pageNo,
              followUp.pageSize,
              followUp.searchParam
            );
          }
          if (data.payload.errorCases) {
            setErrorDisplay(true);
          }
        })
        .catch((e: any) => {
          notification.error({ message: API_SERVICE.handleErrors(e) });
        });
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: "DC Case Id",
      dataIndex: "caseId",
      key: "caseId",
      width: 140,
    },
    {
      title: "Name",
      dataIndex: ["contactDetails, firstName"],
      key: "name",
      ellipsis: true,
      render: (_: any, record: any) => {
        const leadNames = record?.contactDetails?.firstName;
        if ((leadNames?.length ?? 0) > 0) {
          return (
            <div style={{ width: "200px", whiteSpace: "pre-line" }}>
              <text>{leadNames}</text>
            </div>
          );
        }
        return "";
      },
    },
    {
      title: "Follow Up Date",
      dataIndex: "diallerFollowUpDate",
      sorter: true,
      showSorterTooltip:false,
      width: 160,
      sortOrder:
        sortState.field === "diallerFollowUpDate" ? sortState.order : null,
    },
    {
      title: "Mobile No.",
      dataIndex: ["contactDetails", "mobileNumber"],
      key: "mobileNumber",
      width: 130,
      render: colMobileNumber,
    },
    {
      title: "Assign To",
      dataIndex: "assignedToSalesUserName",
      key: "assignedToSalesUserName",
      sorter: true,
      showSorterTooltip:false,
      sortOrder:
        sortState.field === "assignedToSalesUserName" ? sortState.order : null,
    },
    {
      title: "Comment",
      dataIndex: "diallerFollowUpLatestComment",
      key: "diallerFollowUpLatestComment",
      width: 180,
      ellipsis: true,
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      align: "center",
      width: 60,
      render: (_: any, record: any) => {
        let isLoanProduct = (record?.loanProductId?.length ?? 0) == 0;
        return (
          <Space className="action-button" size="small">
            <Tooltip title="Calling option disabled">
              <Button
                className="custome-btn-icon-play"
                style={{ "--bgColor": appColors?.appPrimaryColor ?? "" } as any}
                size="small"
                type="text"
                icon={<IconDialler />}
              />
            </Tooltip>
            <Button
              className="custome-btn-icon-play"
              style={{ "--bgColor": appColors?.appPrimaryColor ?? "" } as any}
              size="small"
              type="text"
              onClick={() => goToOneView(record)}
              icon={<IconCustomer360 />}
            />
            {isLoanProduct ? (
              <div style={{ width: "24px" }}></div>
            ) : (
              <Button
                className="custome-btn-icon-play"
                style={{ "--bgColor": appColors?.appPrimaryColor ?? "" } as any}
                size="small"
                type="text"
                onClick={() => goToLeadDetails(record)}
                icon={<IconPlayNewBorder />}
              />
            )}
          </Space>
        );
      },
    },
  ];

  const renderLeadListItem = (item: any) => {
    let isLoanProduct = (item?.loanProductId?.length ?? 0) == 0;
    const toggleSelection = () => {
      setSelectedLeadIds((prev) =>
        prev.includes(item.caseId)
          ? prev.filter((caseId) => caseId !== item.caseId)
          : [...prev, item.caseId]
      );
    };
    return (
      <>
        <List.Item>
          <Card>
            <div>
              <Row gutter={24} onClick={() => goToLeadDetails(item)}>
                <Col span={12}>
                  <Space>
                    <Checkbox
                      checked={selectedLeadIds.includes(item.caseId)}
                      onChange={() => toggleSelection()}
                      onClick={(e) => e.stopPropagation()}
                      style={{ marginRight: "5px", marginTop: "20px" }}
                    />
                    <label className="leadlist-title">Name</label>
                  </Space>
                  <br />
                  <p style={{ marginLeft: "25px" }}>
                    {item.contactDetails.firstName}
                  </p>
                </Col>
              </Row>
              <Row gutter={24} className="title-space">
                <Col span={12} onClick={() => goToLeadDetails(item)}>
                  <label className="leadlist-title"> DC CaseId</label>
                  <br />
                  <p>{item?.caseId} </p>
                </Col>
                <Col span={12}>
                  <label
                    className="leadlist-title"
                    onClick={() => goToLeadDetails(item)}
                  >
                    {" "}
                    Mobile Number
                  </label>
                  <br />
                  <div>
                    <span onClick={() => goToLeadDetails(item)}>
                      {" "}
                      {getMaskedMobile(
                        userRolePermission["MaskMobileNumber"],
                        item?.contactDetails?.mobileNumber
                      )}
                    </span>
                    <Button
                      size="small"
                      icon={
                        <IconCall
                          style={{
                            marginBottom: "10px", // Adjust the margin value as needed
                          }}
                        />
                      }
                      style={{ marginLeft: "10px", border: "none" }}
                      onClick={() => {
                        if (item?.contactDetails?.mobileNumber) {
                          window.location.href = `tel:${item.contactDetails.mobileNumber}`;
                        } else {
                          alert("Mobile number not available!");
                        }
                      }}
                    />
                  </div>
                </Col>
              </Row>
              <Row
                gutter={24}
                className="title-space"
                onClick={() => goToLeadDetails(item)}
              >
                <Col span={12}>
                  <label className="leadlist-title"> Follow-up Date</label>
                  <br />
                  <p>{item?.followUpDate}</p>
                </Col>
                <Col span={12}>
                  <label className="leadlist-title">Assign To</label>
                  <br />
                  <p> {item?.assignedToSalesUserName}</p>
                </Col>
              </Row>
            </div>
            <Row gutter={14} className="title-space">
              <Col span={8} onClick={() => goToLeadDetails(item)}>
                <label className="leadlist-title">Comment</label>
                <br />
                {item.comment}
              </Col>
              <Col
                span={8}
                className="text-right"
                style={{ paddingLeft: "40px" }}
              >
                <label className="leadlist-title">Action</label>
                <br />
                <Space
                  className="action-button"
                  size="small"
                  style={{ gap: "0px", width: "60px" }}
                >
                  <Button
                    className="custome-btn-icon-play"
                    style={
                      { "--bgColor": appColors?.appPrimaryColor ?? "" } as any
                    }
                    size="small"
                    //onClick={()=>()}
                    icon={<IconDialler />}
                  />
                  <Button
                    className="custome-btn-icon-play"
                    style={
                      { "--bgColor": appColors?.appPrimaryColor ?? "" } as any
                    }
                    size="small"
                    type="text"
                    onClick={() => goToOneView(item)}
                    icon={<IconCustomer360 />}
                  />
                  {isLoanProduct ? (
                    <div style={{ width: "24px" }}></div>
                  ) : (
                    <Button
                      className="custome-btn-icon-play"
                      style={
                        { "--bgColor": appColors?.appPrimaryColor ?? "" } as any
                      }
                      size="small"
                      type="text"
                      onClick={() => goToLeadDetails(item)}
                      icon={<IconPlayNewBorder />}
                    />
                  )}
                </Space>
              </Col>
            </Row>
          </Card>
        </List.Item>
      </>
    );
  };
  const toggleSelectAll = () => {
    if (selectedLeadIds.length === allLeadsData.length) {
      setSelectedLeadIds([]);
    } else {
      const allIds = allLeadsData.map((item) => item.caseId);
      setSelectedLeadIds(allIds);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const sortField = sorter.field;
    const antdSortOrder = sorter.order;
    const apiSortOrder = antdSortOrder === "ascend" ? "ASC" : "DESC";
    setSortState({
      field: sortField,
      order: antdSortOrder,
    });
    if (sortField === "assignedToSalesUserName") {
      assignedToSalesOrderRef.current = apiSortOrder;
      sortFieldRef.current = "ASSIGNED_TO_USER"
    } else {
      diallerFollowUpDateRef.current = apiSortOrder;
      sortFieldRef.current = "DIALLER_DISPOSITION_STATUS_DATETIME"
    }
    getFollowUpLeadsData(
      pagination.current,
      pagination.pageSize,
      pagination.searchValue,
      followUp.filterData
    );
    if(pagination.current != followUp.pageNo || pagination.pageSize != followUp.pageSize){
        handlePaginationChange(pagination);
    }    
  };

  return (
    <>
      {bulkAssignError()}
      {renderOneView()}
      <div className="content-box">
        <Form form={followUpForm} className="app-filter-bar">
          <div className="app-filter-bar-left">
            <Form.Item
              name="searchBox"
              style={{ flex: 1, marginBottom: "0px" }}
            >
              <Input
                size="large"
                className="search-box"
                placeholder="Search"
                onChange={debounce((event) => {
                  if (event?.target?.value?.length > 2) {
                    dispatch(setFollowUpSearchParam(event.target.value));
                    dispatch(setFollowUpPageNo(1));
                  } else if (event?.target?.value?.length == 0) {
                    dispatch(setFollowUpSearchParam(""));
                    dispatch(setFollowUpPageNo(1));
                  }
                }, 600)}
                allowClear
                prefix={<FaSearch />}
              />
            </Form.Item>
            <Button
              type="primary"
              className="filter-btn"
              onClick={() =>
                dispatch(setFollowUpIsShowFilter(!followUp.isShowFilter))
              }
            >
              <img src={blackFilterIcon} alt="" />
              <div
                className="badge-green"
                hidden={
                  !(
                    followUp.filterData?.connectorId ||
                    followUp.filterData?.loanProductIds ||
                    followUp.filterData?.location ||
                    followUp.filterData?.managerHierarchy
                  )
                }
              />
            </Button>
          </div>
        </Form>
        {/* </div> */}
        {followUp.isShowFilter && (
          <LeadFilter key="NEW_LEAD_FILTER" type="FOLLOW_UP" />
        )}
        {isMobile ? (
          <>
            <div>
              <Row gutter={24}>
                <Col span={12}>
                  <Space>
                    <Checkbox
                      checked={selectedLeadIds.length === allLeadsData.length}
                      indeterminate={
                        selectedLeadIds.length > 0 &&
                        selectedLeadIds.length < allLeadsData.length
                      }
                      onChange={() => toggleSelectAll()}
                      style={{ marginRight: "10px" }}
                    />
                    <label className="leadlist-title">All</label>
                  </Space>
                </Col>
              </Row>
            </div>
            <List
              style={{ width: "100%" }}
              grid={{ gutter: 24, column: 1, xs: 1, sm: 1, md: 1, lg: 1 }}
              pagination={{
                onChange: (page, pageSize) => {
                  handleMobilePaginationChange(page, pageSize);
                },
                current: followUp.pageNo,
                pageSize: followUp.pageSize,
                total: totalRecords,
              }}
              itemLayout="horizontal"
              dataSource={allLeadsData}
              loading={loading}
              renderItem={renderLeadListItem}
            />
          </>
        ) : (
          <Table
            className="dynamic-pagination-checkbox"
            style={{ "--bgColor": appColors?.appPrimaryColor ?? "" } as any}
            size="small"
            loading={loading}
            scroll={{ x: "max-content" }}
            dataSource={allLeadsData}
            rowKey={(record) => record?.caseId}
            rowClassName="selected-row"
            rowSelection={rowSelection}
            columns={columns}
            pagination={{
              current: followUp.pageNo,
              pageSize: followUp.pageSize,
              total: totalRecords,
            }}
            onChange={handleTableChange}
          />
        )}
        <div className="assign-to-view">
          <div className="assign-to-view1">
            <Form form={followUpForm}>
              <Form.Item label="Assign To:" name="salesUserId">
                <AutoComplete
                  className="custom-auto-compleat"
                  onSearch={debounce(searchAssignTo, 1000)}
                  placeholder="Search Mobile Number or Name"
                  dropdownClassName="custom-option-list"
                  onSelect={selectAssignTo}
                >
                  {assignToData.map((item: any, i: any) => (
                    <Select.Option
                      key={item.userId}
                      {...item}
                      value={item.fullName}
                    >
                      <Row gutter={[16, 16]} className="assign-list">
                        <Col
                          className="left"
                          span={16}
                          style={{ marginTop: isMobile ? "35px" : "" }}
                        >
                          <span className="name">{item.fullName}</span>
                          <br />
                          <span className="city">
                            {item?.address?.city && item?.address?.city}
                          </span>
                        </Col>
                        <Col className="right" span={8}>
                          <span className="mob">
                            {item?.phoneNumber && item?.phoneNumber}
                          </span>
                        </Col>
                      </Row>
                    </Select.Option>
                  ))}
                </AutoComplete>
                &nbsp;&nbsp;&nbsp;
              </Form.Item>
            </Form>
            <OIButton
              type="primary"
              label="Assign"
              size="small"
              style={{ "--custColor": appColors?.appPrimaryColor } as any}
              onClick={assignSalesUserToLead}
            />
          </div>
        </div>
      </div>
    </>
  );
};
export default FollowUp;
